import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useBedarfeKanban, useUpdateBedarf } from '../../api/bedarfe';
import { byStatusEntryDesc } from '../../utils/kanbanOrder';
import {
  BedarfStatus,
  Bedarf,
  BEDARF_STATUS_ORDER,
  ONBOARDING_STEPS,
  offeneOnboardingSchritte,
} from '../../types/bedarf';
import { useConfirm } from '../../context/ConfirmContext';
import { gewonnenRueckfrage } from '../../utils/gewonnenRueckfrage';
import { KanbanColumn } from './KanbanColumn';
import { KanbanCardOverlay } from './KanbanCard';

const STORAGE_KEY = 'kanban-collapsed-bedarfe';

export interface KanbanBoardFilters {
  search: string;
  status: BedarfStatus | '';
  schoolCity: string;
  assignedUserId: string;
}

interface KanbanBoardProps {
  filters?: KanbanBoardFilters;
}

export function KanbanBoard({ filters }: KanbanBoardProps) {
  const { data, isLoading, error } = useBedarfeKanban();
  const updateMutation = useUpdateBedarf();
  const confirm = useConfirm();
  const [activeBedarf, setActiveBedarf] = useState<Bedarf | null>(null);

  // Collapsed columns state with localStorage persistence
  const [collapsedColumns, setCollapsedColumns] = useState<Set<BedarfStatus>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Save to localStorage when collapsed state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...collapsedColumns]));
  }, [collapsedColumns]);

  const toggleColumnCollapse = useCallback((status: BedarfStatus) => {
    setCollapsedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Custom collision detection that prioritizes columns
  const collisionDetection: CollisionDetection = useCallback((args) => {
    // First, check if pointer is within any droppable
    const pointerCollisions = pointerWithin(args);

    // If we have collisions, prefer column over cards
    if (pointerCollisions.length > 0) {
      // Check if any collision is a column
      const columnCollision = pointerCollisions.find(
        (collision) => BEDARF_STATUS_ORDER.includes(collision.id as BedarfStatus)
      );
      if (columnCollision) {
        return [columnCollision];
      }
      return pointerCollisions;
    }

    // Fall back to rect intersection for better column detection
    return rectIntersection(args);
  }, []);

  // Filter and group bedarfe by status
  const bedarfeByStatus = useMemo(() => {
    const grouped: Record<BedarfStatus, Bedarf[]> = {
      [BedarfStatus.NOCH_NICHT_RELEVANT]: [],
      [BedarfStatus.ANFRAGE]: [],
      [BedarfStatus.OFFEN]: [],
      [BedarfStatus.KENNENLERNGESPRAECH]: [],
      [BedarfStatus.TERMINIERT]: [],
      [BedarfStatus.KOSTENTRAEGER_GO]: [],
      [BedarfStatus.ONBOARDING]: [],
      [BedarfStatus.GEWONNEN]: [],
      [BedarfStatus.VERLOREN]: [],
    };

    let items = data?.data || [];

    // Apply search filter
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      items = items.filter(
        (b) =>
          b.childFirstName?.toLowerCase().includes(searchLower) ||
          b.childLastName?.toLowerCase().includes(searchLower) ||
          b.schoolName?.toLowerCase().includes(searchLower) ||
          b.childCity?.name?.toLowerCase().includes(searchLower) ||
          b.schoolCity?.name?.toLowerCase().includes(searchLower) ||
          b.parentName?.toLowerCase().includes(searchLower)
      );
    }

    // Apply school city filter (by stadt ID)
    if (filters?.schoolCity) {
      items = items.filter((b) => b.schoolCity?.id === filters.schoolCity);
    }

    // Apply assigned-user filter
    if (filters?.assignedUserId) {
      items = items.filter((b) => b.assignedToId === filters.assignedUserId);
    }

    // Apply status filter
    if (filters?.status) {
      items = items.filter((b) => b.status === filters.status);
    }

    for (const bedarf of items) {
      if (grouped[bedarf.status]) {
        grouped[bedarf.status].push(bedarf);
      }
    }

    // Every column: last moved in at the top. Editing a card does not move it.
    for (const status of Object.keys(grouped) as BedarfStatus[]) {
      grouped[status].sort(byStatusEntryDesc);
    }

    return grouped;
  }, [data?.data, filters]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const bedarf = data?.data.find((b) => b.id === active.id);
    if (bedarf) {
      setActiveBedarf(bedarf);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveBedarf(null);

    if (!over) return;

    const bedarfId = active.id as string;
    const bedarf = data?.data.find((b) => b.id === bedarfId);

    if (!bedarf) return;

    // Determine the target status from the drop zone
    let newStatus: BedarfStatus | null = null;

    // Check if dropped on a column
    if (BEDARF_STATUS_ORDER.includes(over.id as BedarfStatus)) {
      newStatus = over.id as BedarfStatus;
    } else {
      // Dropped on another card - find its column
      const overBedarf = data?.data.find((b) => b.id === over.id);
      if (overBedarf) {
        newStatus = overBedarf.status;
      }
    }

    // Only update if status changed
    if (newStatus && newStatus !== bedarf.status) {
      // Open onboarding steps no longer block GEWONNEN, they ask first. The card
      // stays in its column until the answer is yes.
      if (newStatus === BedarfStatus.GEWONNEN) {
        const rueckfrage = gewonnenRueckfrage(
          offeneOnboardingSchritte(bedarf),
          ONBOARDING_STEPS.length,
          'bedarf',
        );
        if (rueckfrage && !(await confirm(rueckfrage))) return;
      }
      updateMutation.mutate({
        id: bedarfId,
        data: { status: newStatus },
      });
    }
  };

  const handleDragCancel = () => {
    setActiveBedarf(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Fehler beim Laden der Bedarfe: {error.message}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 min-h-[calc(100vh-220px)] lg:min-h-0 lg:h-full">
        {BEDARF_STATUS_ORDER.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            bedarfe={bedarfeByStatus[status]}
            isCollapsed={collapsedColumns.has(status)}
            onToggleCollapse={() => toggleColumnCollapse(status)}
          />
        ))}
      </div>

      <DragOverlay>
        {activeBedarf && <KanbanCardOverlay bedarf={activeBedarf} />}
      </DragOverlay>
    </DndContext>
  );
}
